import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAPIClient } from '@/hooks/useAPIClient';
import { SessionResponse } from '@/types/api';
import { useToast } from '@/hooks/use-toast';
import { handleAPIError } from '@/services/api/errorHandler';

export const useSessions = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const apiClient = useAPIClient();

  // Query for fetching sessions list
  const sessionsQuery = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => {
      try {
        const result = await apiClient.sessions.list();
        return result;
      } catch (error) {
        console.error('Sessions API error:', error);
        throw error;
      }
    },
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchInterval: 60000, // Refetch every minute
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Mutation for creating a new session
  const createSession = useMutation({
    mutationFn: apiClient.sessions.create,
    onSuccess: (newSession) => {
      // Update the sessions list cache
      queryClient.setQueryData(['sessions'], (old: SessionResponse[] = []) => {
        return [...old, newSession];
      });
      
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (error) => {
      const errorMessage = handleAPIError(error);
      toast({
        title: "Erstellen fehlgeschlagen",
        description: errorMessage,
        variant: "destructive",
        duration: 5000,
      });
    }
  });

  // Mutation for deleting a session
  const deleteSession = useMutation({
    mutationFn: apiClient.sessions.delete,
    onMutate: async (sessionId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['sessions'] });

      // Snapshot the previous value
      const previousSessions = queryClient.getQueryData<SessionResponse[]>(['sessions']);

      // Optimistically update by removing the session
      queryClient.setQueryData(['sessions'], (old: SessionResponse[] = []) => {
        return old.filter(s => s.session_id !== sessionId);
      });

      // Return a context object with the snapshotted value
      return { previousSessions };
    },
    onSuccess: () => {
      // Session deleted - no notification needed
    },
    onError: (error, sessionId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousSessions) {
        queryClient.setQueryData(['sessions'], context.previousSessions);
      }

      const errorMessage = handleAPIError(error);
      toast({
        title: "Löschen fehlgeschlagen",
        description: errorMessage,
        variant: "destructive",
        duration: 5000,
      });
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    }
  });

  // Mutation for clearing all sessions
  const clearAllSessions = useMutation({
    mutationFn: () => apiClient.sessions.clearAll("CONFIRM_DELETE_ALL"),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['sessions'] });

      // Snapshot the previous value
      const previousSessions = queryClient.getQueryData<SessionResponse[]>(['sessions']);

      // Return a context object with the snapshotted value
      return { previousSessions };
    },
    onSuccess: (data) => {
      // Set query data to only contain the new session
      queryClient.setQueryData(['sessions'], () => [{
        session_id: data.new_session.session_id,
        title: data.new_session.title,
        created_at: data.new_session.created_at,
        message_count: 1,
        last_activity: data.new_session.created_at
      }]);

      toast({
        title: "Sitzungen gelöscht",
        description: `${data.deleted_count} Sitzungen entfernt`,
        duration: 3000,
      });
    },
    onError: (error, _, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousSessions) {
        queryClient.setQueryData(['sessions'], context.previousSessions);
      }

      const errorMessage = handleAPIError(error);
      toast({
        title: "Löschen fehlgeschlagen",
        description: errorMessage,
        variant: "destructive",
        duration: 5000,
      });
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    }
  });

  return {
    sessions: sessionsQuery.data || [],
    isLoading: sessionsQuery.isLoading,
    isError: sessionsQuery.isError,
    error: sessionsQuery.error,
    refetch: sessionsQuery.refetch,
    createSession,
    deleteSession,
    clearAllSessions,
    isCreating: createSession.isPending,
    isDeleting: deleteSession.isPending,
    isClearingAll: clearAllSessions.isPending,
  };
};